import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

const API_URL = import.meta.env.VITE_API_URL;
const appIdUri = import.meta.env.VITE_APP_ID_URI;
 
class DataService {
  constructor(msalInstance, account) {
    this.msalInstance = msalInstance;
    this.account = account;
  }

  async getToken() {

    const request = {
      scopes: [appIdUri+"/.default"],
      account: this.account
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // fallback to interacting with the user
        const response = await this.msalInstance.acquireTokenPopup(request);
        return response.accessToken;
      }
    }

  }

  async updateHaulerPools(hauler,selectedPoolIds) {
    const token = await this.getToken();
    return axios.post(`${API_URL}/Haulers/${hauler.haulerId}/update-pools`, {selectedPoolIds,hauler},{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    });
  }

  async updateLivestockCenterPools(selectedPools) {
    const token = await this.getToken();
    const id = selectedPools[0].livestockCenterId;
    return axios.post(`${API_URL}/LivestockCenters/${id}/update-pools`, selectedPools,{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    });
  }

  async updateSlaughterPlantPools(slaughterPlant,selectedPoolIds) {
    const token = await this.getToken();
    return axios.post(`${API_URL}/SlaughterPlants/${slaughterPlant.slaughterPlantId}/update-pools`, {selectedPoolIds,slaughterPlant},{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    });
  }

  async getPriceAttributesGroupedByAttribute() {
    try {
      const token = await this.getToken();
      const response = await axios.get(`${API_URL}/priceattributes/grouped-by-attribute`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getPriceAttributesForEdit(priceAttribute) {
    try {
      const token = await this.getToken();
      const response = await axios.get(`${API_URL}/priceattributes/for-edit`, {
        headers: {
            'Authorization': `Bearer ${token}`
        },
        params: {
          livestockTypeId: String(priceAttribute.livestockTypeId).trim(),
          animalTypeId: String(priceAttribute.animalTypeId).trim(),
          animalGradeId: priceAttribute.animalGradeId != null ? String(priceAttribute.animalGradeId).trim() : null,
          yield: priceAttribute.yield != null ? String(priceAttribute.yield).trim() : null,
          attributeId: String(priceAttribute.attributeId).trim(),
      }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  async getSettingsForEdit(setting) {
    try {
      const token = await this.getToken();
      const response = await axios.get(`${API_URL}/settings/for-edit`, {
        headers: {
            'Authorization': `Bearer ${token}`
        },
        params: {
          attributeId: String(setting.attributeId).trim(), 
      }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getProducerContactsByProducerId(producerId) {
    try {
      const token = await this.getToken();
      const response = await axios.get(`${API_URL}/producercontacts/by-producerId`, {
        headers: {
            'Authorization': `Bearer ${token}`
        },
        params: {
          producerId: String(producerId).trim()        
       }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getScheduleCards(livestockTypeId,startDate,endDate) {
    try {
      const token = await this.getToken();   
      const response = await axios.get(`${API_URL}/schedules/cards`, {
        headers: {
            'Authorization': `Bearer ${token}`
        },
        params: {
          livestockTypeId: String(livestockTypeId).trim(),
          startDate: String(startDate).trim(),
          endDate: String(endDate).trim(),

       }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAll(entity) {
    const token = await this.getToken();
    return axios.get(`${API_URL}/${entity}`,{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    })
    .then(response => 
      response.data
    );
  }

  async get(entity,id) {
    const token = await this.getToken();
    return axios.get(`${API_URL}/${entity}/${id}`,{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    })
    .then(response => response.data);
  }

  async create(entity,data) {
    const token = await this.getToken();
    return axios.post(`${API_URL}/${entity}`, data,{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    });
  }

  async update(entity,data) {
    const token = await this.getToken();
    return axios.put(`${API_URL}/${entity}`, data,{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    });
  }

  async delete(entity,id) {
    const token = await this.getToken();
    return axios.delete(`${API_URL}/${entity}/${id}`,{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    });
  }

  async notifyAndDownload(pool,scheduleId,userId) {
    const token = await this.getToken();

    const response = await axios.get(`${API_URL}/schedules/${scheduleId}/notify-${pool}/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      responseType: 'blob', // important for binary data like CSV
    });

    // Create a URL for the blob and simulate a download
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/xlsx' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Schedule_${scheduleId}_${pool}_Details.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async createHaulerNotificationFile(scheduleId) {
    const token = await this.getToken();
    const response = await axios.get(`${API_URL}/schedules/${scheduleId}/notify-hauler`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      responseType: 'blob', // important for binary data like CSV
    });

    // Create a URL for the blob and simulate a download
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/xlsx' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Schedule_${scheduleId}_Hauler.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

 async createHaulerReport(scheduleId) {
  const token = await this.getToken();

  const response = await axios.get(`${API_URL}/schedules/${scheduleId}/hauler-report`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: 'blob', // ✅ still needed for binary data
  });

  // ✅ Use correct MIME type for PDF
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `Schedule_${scheduleId}_Hauler.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

 async importSlaughterFile(pool,scheduleId,userId,file) {
    const token = await this.getToken();

     const formData = new FormData();
     formData.append('file', file);  
     const url = `${API_URL}/schedules/${scheduleId}/import-${pool}/${userId}`;

     const response = await axios.post(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          // DO NOT manually set 'Content-Type' — let Axios set the boundary
        },
      });

    return response.data;
    
  }

 async CertifyLivestock(certifyLivestock) {
     const token = await this.getToken();
    return axios.post(`${API_URL}/scheduledetails/certify`, certifyLivestock,{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    });
    
  }

  async UncertifyLivestock(certifyLivestock) {
     const token = await this.getToken();
    return axios.post(`${API_URL}/scheduledetails/un-certify`, certifyLivestock,{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    });
    
  }

  async VerifyProducer(scheduleDetailId,username) {
    const token = await this.getToken();
   return axios.post(`${API_URL}/scheduledetails/verify/${scheduleDetailId}`, username, {  
     headers: {
       'Authorization': `Bearer ${token}`,
       'Content-Type': 'application/json'
   }
  });   
 }

 async UnverifyProducer(scheduleDetailId,username) {
    const token = await this.getToken();
   return axios.post(`${API_URL}/scheduledetails/un-verify/${scheduleDetailId}`, username, {
   headers: {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
    }
    });   
  }


  async getSchedulePayableSummaries(livestockTypeId,plantId,externalPlantId) {
    try {
      const token = await this.getToken();   
        console.log('Fetching schedule payable summaries:'); 
      const response = await axios.get(`${API_URL}/schedules/payable-summaries`, {
        headers: {
            'Authorization': `Bearer ${token}`
        },
        params: {
          livestockTypeId: String(livestockTypeId).trim(),
          slaughterPlantId: String(plantId).trim(),
          slaughterPlantExternalId: String(externalPlantId).trim(),
       }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async CreatePayable(pool,scheduleIds, username) {
     const token = await this.getToken();
    return axios.post(`${API_URL}/scheduledetails/create-${pool}-payable?username=${encodeURIComponent(username)}`,
       scheduleIds,{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    });
    
  }

  async ApprovePayable(producerPayableIds, username) {
     const token = await this.getToken();
    return axios.post(`${API_URL}/scheduledetails/approve-payable?username=${encodeURIComponent(username)}`,
    producerPayableIds,{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    });
    
  }

  async CreateProducerPayableInvoice(producerPayableId, username) {
    const token = await this.getToken();
   return axios.post(`${API_URL}/scheduledetails/create-invoice/${producerPayableId}?username=${encodeURIComponent(username)}`,
   {
     headers: {
       'Authorization': `Bearer ${token}`
   }
   });
   
 }

  async CreateInvoice(producerPayableId, username) {
    const token = await this.getToken();
   return axios.post(`${API_URL}/scheduledetails/create-invoice/${producerPayableId}?username=${encodeURIComponent(username)}`,
     {
     headers: {
       'Authorization': `Bearer ${token}`
   }
   });
   
 }

  async UnapprovePayable(producerPayableIds, username) {
     const token = await this.getToken();
    return axios.post(`${API_URL}/scheduledetails/unapprove-payable?username=${encodeURIComponent(username)}`,
    producerPayableIds,{
      headers: {
        'Authorization': `Bearer ${token}`
    }
    });
    
  }

  async createProducerPayableReport(scheduleDetailId, poolCode) {
    const token = await this.getToken();

    try {
      const response = await axios.get(`${API_URL}/scheduledetails/producer-${poolCode}-payable-report/${scheduleDetailId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'text', // Changed to text for HTML content
      });

      // Check if response is HTML (fallback) or PDF
      const contentType = response.headers['content-type'] || '';
      
      if (contentType.includes('text/html')) {
        // HTML fallback - open in new window
        const newWindow = window.open('', '_blank');
        newWindow.document.write(response.data);
        newWindow.document.close();
        
        // Add print button to the new window
        newWindow.document.addEventListener('DOMContentLoaded', function() {
          const printButton = document.createElement('button');
          printButton.innerHTML = '🖨️ Print to PDF';
          printButton.style.cssText = `
            position: fixed;
            top: 10px;
            right: 10px;
            z-index: 9999;
            background: #007bff;
            color: white;
            border: none;
            padding: 10px 15px;
            border-radius: 5px;
            cursor: pointer;
            font-size: 14px;
          `;
          printButton.onclick = () => window.print();
          document.body.appendChild(printButton);
        });
      } else {
        // PDF response (if available in future)
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Producer_Payable_Report_${scheduleDetailId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error generating producer payable report:', error);
      alert('Error generating report. Please try again or contact support.');
    }
  }

  async searchProducerPayableHistory(producerId, startDate, endDate, livestockTypeId) {
    try {
      const token = await this.getToken();
      
      // Build params object, only including producerId if it's not null
      const params = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        livestockTypeId: String(livestockTypeId).trim()
      };
      
      // Only add producerId to params if it's not null
      if (producerId !== null && producerId !== undefined) {
        params.producerId = String(producerId).trim();
      }
      
      const response = await axios.get(`${API_URL}/scheduledetails/producer-payable-history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: params
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

}

export default DataService;